import React, { useMemo } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./CardsRow.css"; // Import the updated CSS file for styling
import { isSummaryDataReady } from "./summaryCardReadiness";
import { buildSummaryCards } from "./cardFlowRows";
import CardFlowRows from "./CardFlowRows.jsx";

const toTestId = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/* Row-shaped counterpart to CardsPage's StatusCardSkeleton: same four cards,
 * laid out by .status-card-row rather than the power-flow grid, so the header
 * keeps its height while the simulation is still reporting. */
const StatusCardRowSkeleton = () => (
  <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
    <div className="status-card-row" data-testid="sticky-summary-cards-skeleton">
      {[
        { cardClass: "transfer", iconBg: "transfer-bg" },
        { cardClass: "production", iconBg: "prod-bg" },
        { cardClass: "consumption", iconBg: "consum-bg" },
        { cardClass: "storage", iconBg: "storage-bg" },
      ].map(({ cardClass, iconBg }) => (
        <div
          key={cardClass}
          className={`status-card-grid-item shadow ${cardClass}`}
          aria-hidden="true"
        >
          <div className="status-card-content">
            <div className="graph-name d-flex">
              <div className="sc-card-title">
                <Skeleton width={120} height={24} style={{ marginBottom: "8px" }} />
              </div>
              <div className="sc-card-icon">
                <div
                  className={`icon-circle ${iconBg} d-flex justify-content-center align-items-center`}
                >
                  <Skeleton width={24} height={24} circle />
                </div>
              </div>
            </div>
            <div className="cost d-flex justify-content-center">
              <Skeleton width={100} height={32} style={{ marginBottom: "8px" }} />
            </div>
            <div className="stats d-flex">
              <div className="stat-item-storage-left">
                <Skeleton width={80} height={16} style={{ marginBottom: "4px" }} />
                <Skeleton width={60} height={20} />
              </div>
              <div className="stat-item-storage-right">
                <Skeleton width={80} height={16} style={{ marginBottom: "4px" }} />
                <Skeleton width={60} height={20} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </SkeletonTheme>
);

export default function StatusCardRow({
  shared,
  exportedData,
  exportedProductionData,
  exportedProductionSummaryData,
  exportedStorageData,
  exportedStorageSummaryData,
  exportedTransferData,
  exportedDemandChargeData,
  /* Whole-site summaries for the site-net line under Transfer. This row's
   * cards follow device selection; the site net cannot. */
  siteProductionSummaryData,
  siteStorageSummaryData,
  onCardHoverChange,
  onCardToggle,
  onCardPointerDown,
  summaryCardVisibility,
  chartDataReadyStates,
  shouldShowCards,
  hasMeaningfulData,
}) {
  /* This row had no readiness gate at all: it rendered straight through and
   * fell back to "0.00" strings, so the Charts tab header showed a full set of
   * fabricated zeros from the first paint. It shares CardsPage's gate rather
   * than carrying its own -- both surfaces render the same four cards off the
   * same context, so two rules would be two things to keep in step. */
  const dataReady = isSummaryDataReady({
    chartDataReadyStates,
    shouldShowCards,
    hasMeaningfulData,
  });

  const productionCardData =
    exportedProductionSummaryData || exportedProductionData;
  const storageCardData = exportedStorageSummaryData || exportedStorageData;

  const summaryData = useMemo(
    () =>
      buildSummaryCards({
        exportedData,
        productionData: productionCardData,
        storageData: storageCardData,
        transferData: exportedTransferData,
        demandData: exportedDemandChargeData,
        siteProductionData: siteProductionSummaryData || productionCardData,
        siteStorageData: siteStorageSummaryData || storageCardData,
      }),
    [
      exportedData,
      productionCardData,
      storageCardData,
      exportedTransferData,
      exportedDemandChargeData,
      siteProductionSummaryData,
      siteStorageSummaryData,
    ],
  );

  if (!dataReady) {
    return <StatusCardRowSkeleton />;
  }

  return (
    <div className="status-card-row">
      {summaryData.map((item, index) => {
        return (
          <div
            key={index}
            className={`status-card-grid-item shadow ${item.title.toLowerCase()} ${
              summaryCardVisibility?.[item.title] === false
                ? "summary-card--inactive"
                : "summary-card--active"
            }`}
            data-testid={`sticky-summary-card-${toTestId(item.title)}`}
            data-card-title={item.title}
            data-card-surface="sticky"
            data-card-active={
              summaryCardVisibility?.[item.title] === false ? "false" : "true"
            }
            role="button"
            tabIndex={0}
            aria-pressed={summaryCardVisibility?.[item.title] === false ? "false" : "true"}
            onPointerDown={(event) => {
              onCardPointerDown?.(item.title, event.currentTarget);
              event.preventDefault();
            }}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => onCardHoverChange?.(item.title)}
            onMouseLeave={() => onCardHoverChange?.(null)}
            onClick={(event) => {
              if (event.detail !== 0) {
                event.currentTarget.blur?.();
              }
              onCardToggle?.(item.title);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onCardPointerDown?.(item.title, event.currentTarget);
                onCardToggle?.(item.title);
              }
            }}
          >
            <div className="status-card-content">
              <div className="graph-name d-flex">
                <div className="sc-card-title">
                  {/* The label only. Everything else on this card keys off
                    * `item.title`, which stays "Transfer" -- see
                    * cardFlowRows.js. */}
                  <span className="graph-title">{item.displayTitle}</span>
                </div>
                <div className="sc-card-icon">
                  <div
                    className={`icon-circle ${item.iconBg} d-flex justify-content-center align-items-center`}
                  >
                    <i
                      className={item.iconClass}
                      style={{ color: item.iconColor }}
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>
              </div>

              <CardFlowRows
                rows={item.rows}
                total={item.price}
                totalIncludesDemand={item.priceIncludesDemand}
                totalLabel={item.totalLabel}
                reconciliation={item.reconciliation}
                testIdPrefix={`sticky-summary-card-${toTestId(item.title)}`}
                showColumnHeaders
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
